import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { FullWidthLoader } from "../../components/full-width-loader";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { SECTION } from "../../lib/constants";
import { useInvalidateInventoryCaches } from "../inventory/inventory.hook";
import { CategoryLimitsProvider } from "../extrasCategories/presentation/context/CategoryLimitsContext";
import { SaveWarningsDialog } from "../extrasCategories/presentation/components/SaveWarningsDialog";
import SimpleCrud from "../shared/crud/simple-crud";
import { getDataByActions, Image, ImageManagerProvider } from "../shared/widgets/ImageManager";
import { accommodationQueryKeys } from "./accommodation.queryKeys";
import { NoImagesWarning } from "./components/NoImagesWarning";
import { AccommodationEntity, AccommodationForm } from "./domain/Accommodation.entity";
import { AccommodationObject, useAccommodationApi } from "./infrastructure/api/accommodation.api";
import { useBackofficeApi } from "./infrastructure/api/backoffice.api";
import { usePropertyConfigurationApi } from "./infrastructure/api/propertyConfiguration.api";
import { useAccommodationCategoryLimits } from "./infrastructure/hooks/categoryLimits.hook";
import { useFormConfiguration } from "./infrastructure/hooks/formConfiguration.hook";
import { useTableColumns } from "./infrastructure/hooks/tableColumns.hook";
import { asGrpc } from "./infrastructure/mappers/grpc.mapper";
import { asObject } from "./infrastructure/mappers/toObject.mapper";
import { messages } from "./messages";
import { FormContainer } from "./styles";

function AccommodationCrud({ propertyTicker, intl }: { propertyTicker: string; intl: IntlShape }) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccommodationModel, setHasAccommodationModel] = useState(false);
  const [accommodations, setAccommodations] = useState<AccommodationObject[]>([]);
  const queryClient = useQueryClient();
  // Rest
  const propertyConfig = usePropertyConfiguration();
  const isOrchestratorActive = propertyConfig?.["isOrchestratorActive"] === "1";
  const { isAccommodationModel } = usePropertyConfigurationApi();
  const { createImages, updateImages, removeImages, regenCache } = useBackofficeApi(propertyTicker);
  // GRPC API
  const {
    getAccommodation,
    getAccommodations,
    createAccommodation,
    updateAccommodation,
    deleteAccommodation,
    reorderAccommodation,
  } = useAccommodationApi();

  // Limits per category: inert while the flag is off or the section is not visible.
  const {
    isLoading: isLoadingCategoryLimits,
    schema,
    formOptions: categoryLimitsFormOptions,
    context: categoryLimitsContext,
    warningsDialog,
    withInitialState,
    onFormChange,
    addValidationErrors: addCategoryLimitsErrors,
    requestInput: categoryLimitsInput,
    onSaveStarted,
    onSaveRejected,
    onSaved,
  } = useAccommodationCategoryLimits(intl.locale);

  const tableColumns = useTableColumns();
  const formConfiguration = useFormConfiguration(hasAccommodationModel, categoryLimitsFormOptions);
  const invalidateInventoryCaches = useInvalidateInventoryCaches();

  const invalidateAccommodations = useCallback(() => {
    queryClient.removeQueries({ queryKey: accommodationQueryKeys.list(propertyTicker) });
    return invalidateInventoryCaches();
  }, [queryClient, propertyTicker, invalidateInventoryCaches]);

  const fetchUploadImages = useCallback(
    async ({ id, multimediaList }: AccommodationEntity) => {
      const currentAccommodation = accommodations.find(it => it.id === id);
      if (!currentAccommodation) {
        return await createImages(
          id,
          multimediaList.map(it => (it as unknown) as Image)
        );
      }
      const { create, update, remove } = getDataByActions(
        currentAccommodation.multimediaList.map(it => (it as unknown) as Image),
        multimediaList.map(it => (it as unknown) as Image)
      );

      if (isOrchestratorActive) {
        await removeImages(id, remove);
        const created = await createImages(id, create);
        if (update.length) {
          await updateImages(id, [...update, ...created]);
        }
        return;
      }

      return await Promise.all([
        createImages(id, create),
        removeImages(id, remove),
        updateImages(id, update),
      ]);
    },
    [accommodations, createImages, isOrchestratorActive, removeImages, updateImages]
  );

  const handleGetAll = useCallback(async (): Promise<AccommodationEntity[]> => {
    const accommodations = await queryClient.ensureQueryData({
      queryKey: accommodationQueryKeys.list(propertyTicker),
      queryFn: () => getAccommodations(),
      staleTime: 5 * 60 * 1000,
    });
    setAccommodations(accommodations);
    return accommodations.map(it => asObject(it));
  }, [queryClient, propertyTicker, getAccommodations, setAccommodations]);

  const handleGetOne = useCallback(
    async (id: string) => {
      const accommodation = await getAccommodation(id);
      setAccommodations([accommodation]);
      return withInitialState(asObject(accommodation));
    },
    [getAccommodation, setAccommodations, withInitialState]
  );

  const handleCreate = useCallback(
    async (entity: AccommodationForm) => {
      onSaveStarted();
      try {
        const { warnings } = await createAccommodation(asGrpc(entity), categoryLimitsInput(entity));
        await fetchUploadImages(entity);
        await regenCache();
        invalidateAccommodations();
        await onSaved(warnings);
        return entity.id;
      } catch (e) {
        console.error("Error during create accommodation", e);
        throw new Error(onSaveRejected(e) ?? intl.formatMessage(messages.saveError));
      }
    },
    [
      categoryLimitsInput,
      createAccommodation,
      fetchUploadImages,
      intl,
      invalidateAccommodations,
      onSaveRejected,
      onSaveStarted,
      onSaved,
      regenCache,
    ]
  );

  const handleUpdate = useCallback(
    async (entity: AccommodationForm) => {
      const grpcEntity = asGrpc(entity, { includeMultimedia: !isOrchestratorActive });
      onSaveStarted();
      try {
        const { warnings } = await updateAccommodation(grpcEntity, categoryLimitsInput(entity));
        await fetchUploadImages(entity);
        await regenCache();
        invalidateAccommodations();
        await onSaved(warnings);
      } catch (e) {
        console.error("Error during update accommodation", e);
        throw new Error(onSaveRejected(e) ?? intl.formatMessage(messages.saveError));
      }
    },
    [
      categoryLimitsInput,
      fetchUploadImages,
      isOrchestratorActive,
      regenCache,
      updateAccommodation,
      intl,
      invalidateAccommodations,
      onSaveRejected,
      onSaveStarted,
      onSaved,
    ]
  );

  const handleDelete = useCallback(
    async (entityId: string) => {
      try {
        await deleteAccommodation(entityId);
        await regenCache();
        invalidateAccommodations();
      } catch (e) {
        console.error("Error during delete accommodation", e);
        throw e;
      }
    },
    [deleteAccommodation, invalidateAccommodations, regenCache]
  );

  const handleReorder = useCallback(
    async (id: string, after?: string) => {
      try {
        await reorderAccommodation(id, after || "");
        await regenCache();
        invalidateAccommodations();
      } catch (e) {
        console.error("Error during reorder accommodation", e);
        throw e;
      }
    },
    [reorderAccommodation, regenCache, invalidateAccommodations]
  );

  const validate = useCallback(
    (formData, errors) => {
      if (!formData.multimediaList || formData.multimediaList.length < 1) {
        errors.multimediaList.addError(intl.formatMessage(messages.requiredImages));
      }
      return addCategoryLimitsErrors(formData, errors);
    },
    [intl, addCategoryLimitsErrors]
  );

  useEffect(() => {
    setIsLoading(true);
    isAccommodationModel()
      .then(setHasAccommodationModel)
      .finally(() => setIsLoading(false));
  }, [isAccommodationModel]);

  // The section's initial state must be known before the form mounts.
  if (isLoading || isLoadingCategoryLimits) {
    return <FullWidthLoader />;
  }

  return (
    <CategoryLimitsProvider value={categoryLimitsContext}>
      <ImageManagerProvider>
        {/* Warning component for accommodations without images */}
        <NoImagesWarning accommodations={accommodations} />

        <SimpleCrud<AccommodationForm>
          formContainer={FormContainer}
          messages={messages}
          onGetAll={handleGetAll}
          onGetOne={handleGetOne}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onReorder={handleReorder}
          formConfiguration={formConfiguration}
          schema={schema}
          onChange={onFormChange}
          tableColumns={tableColumns}
          section={SECTION.ACCOMMODATIONS}
          validate={validate}
        />
        <SaveWarningsDialog {...warningsDialog} />
      </ImageManagerProvider>
    </CategoryLimitsProvider>
  );
}

export default injectIntl(withAuthorizationVerifier(AccommodationCrud));
